// Open orders of the stores served by this gate — anything that may still send a truck. A vendor
// delivers in as many consignments as suits them, so each card says how many came so far (and if
// one is waiting at the gate now), not when "the" delivery is due. Tapping one opens the order to
// record a consignment — for a driver without the printed gate pass.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  ChevronRight,
  DoorOpen,
  Hourglass,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  Truck,
  X,
} from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, type OrderRow, type OrdersResponse } from "@/lib/gate";
import { useGate } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

type Tab = "all" | "waiting" | "progress" | "notStarted";
const TABS: { key: Tab; label: string; test: (r: OrderRow) => boolean }[] = [
  { key: "all", label: "All", test: () => true },
  { key: "waiting", label: "At the gate now", test: (r) => r.waitingCount > 0 },
  { key: "progress", label: "Partly delivered", test: (r) => r.stage === "IN_PROGRESS" },
  { key: "notStarted", label: "Nothing yet", test: (r) => r.stage === "NOT_STARTED" },
];

export default function Orders() {
  const { token } = useAuth();
  const { gate } = useGate();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState<OrdersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("all");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const h = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reload on gate / search / refresh
    setLoading(true);
    setError(null);
    gateApi
      .orders(token, gate.id, query, controller.signal)
      .then((d) => setData(d ?? { rows: [], stores: [], truncated: false }))
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(e instanceof ApiError ? e.message : "Could not load the open orders.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, gate.id, query, nonce]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const current = TABS.find((x) => x.key === tab) ?? TABS[0];
  const shown = rows.filter(current.test);

  return (
    <div className="w-full pb-8">
      <div className="flex items-center gap-3 px-4 pt-4 pb-1 md:px-6">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <DoorOpen className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-base leading-tight font-semibold">{gate.name}</div>
          <div className="line-clamp-2 text-xs text-muted-foreground">
            {[
              gate.operationUnit?.name,
              data && data.stores.length > 0 ? `Serves ${data.stores.map((x) => x.name).join(", ")}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        {data && data.stores.length > 0 && (
          <div className="shrink-0 text-right">
            <div className="text-xl leading-none font-bold tabular-nums">{rows.length}</div>
            <div className="text-[11px] text-muted-foreground">open orders</div>
          </div>
        )}
      </div>

      <div className="sticky top-0 z-30 flex flex-col gap-2 border-b border-border bg-muted/95 px-4 pt-3 pb-2 backdrop-blur md:flex-row md:items-center md:gap-3 md:px-6">
        <div className="flex gap-2 md:w-[26rem] md:shrink-0">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Vendor, PO number or store"
              className="h-11 rounded-xl bg-card pr-9 pl-9 text-base md:text-base"
              enterKeyHint="search"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Clear search"
                className="absolute top-1/2 right-1 -translate-y-1/2"
                onClick={() => setSearch("")}
              >
                <X />
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            aria-label="Refresh"
            className="size-11 rounded-xl bg-card"
            onClick={() => setNonce((n) => n + 1)}
          >
            {loading ? <Loader2 className="size-5 animate-spin" /> : <RefreshCw className="size-5" />}
          </Button>
        </div>
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 md:mx-0 md:px-0">
          {TABS.map((x) => (
            <Button
              key={x.key}
              variant="outline"
              onClick={() => setTab(x.key)}
              className={cn(
                "h-9 shrink-0 gap-1.5 rounded-full bg-card px-3.5 text-sm",
                tab === x.key &&
                  "border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
              )}
            >
              {x.label}
              <span
                className={cn(
                  "text-xs tabular-nums",
                  tab === x.key ? "text-primary-foreground/80" : "text-muted-foreground",
                )}
              >
                {rows.filter(x.test).length}
              </span>
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 pt-4 md:px-6">
        {error ? (
          <p className="rounded-xl bg-destructive/10 p-4 text-sm text-destructive">{error}</p>
        ) : loading && !data ? (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-28 w-full rounded-2xl" />
            ))}
          </div>
        ) : data && data.stores.length === 0 ? (
          <Empty
            title={`No store uses ${gate.name} yet`}
            text="A store subscribes from its operating profile in the Store App (Settings → Operating profile → “Deliveries come through these gates”)."
          />
        ) : shown.length === 0 ? (
          <Empty
            title={query ? "Nothing matches" : "No open orders here"}
            text={
              query
                ? "Try another vendor, PO number or store."
                : `Orders of the stores served by ${gate.name} that may still send a truck appear here.`
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {shown.map((r) => (
              <OrderCard key={r.id} row={r} onOpen={() => navigate(`/order/${r.id}`)} />
            ))}
          </div>
        )}
        {data?.truncated && (
          <p className="text-center text-xs text-muted-foreground">
            Showing the latest 300 — search to narrow the list.
          </p>
        )}
      </div>
    </div>
  );
}

function OrderCard({ row, onOpen }: { row: OrderRow; onOpen: () => void }) {
  const where = row.destinations
    .map((d) => [d.department, d.deliverySites?.join("; ")].filter(Boolean).join(" — "))
    .filter(Boolean)
    .join(" | ");
  return (
    <Button
      variant="outline"
      onClick={onOpen}
      className={cn(
        "relative h-auto w-full items-stretch gap-3 overflow-hidden rounded-2xl bg-card py-3 pr-2 pl-4 text-left font-normal whitespace-normal shadow-xs active:scale-[0.99]",
        row.waitingCount > 0 && "border-amber-300 dark:border-amber-800",
      )}
    >
      <span
        className={cn(
          "absolute inset-y-0 left-0 w-1.5",
          row.waitingCount > 0 ? "bg-amber-500" : row.stage === "IN_PROGRESS" ? "bg-primary" : "bg-muted-foreground/30",
        )}
        aria-hidden
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-base leading-snug font-semibold">{row.vendor ?? "—"}</span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
          <span className="font-mono text-foreground">{row.poNumber}</span>
          <span>·</span>
          <span>{row.store.name}</span>
          <span>·</span>
          <span>
            {row.itemCount} item{row.itemCount === 1 ? "" : "s"}
          </span>
        </span>
        {where && (
          <span className="flex items-start gap-1 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 size-3.5 shrink-0" />
            <span className="line-clamp-2">{where}</span>
          </span>
        )}
        <span className="flex flex-wrap items-center gap-1.5">
          {row.waitingCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <Hourglass className="size-3.5" /> {row.waitingCount} at the gate now
            </span>
          )}
          {row.consignmentCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
              <Truck className="size-3.5" /> {row.consignmentCount} consignment{row.consignmentCount === 1 ? "" : "s"} ·
              last {row.lastConsignmentAt}
            </span>
          ) : (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
                row.firstDeliveryLate ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground",
              )}
            >
              <CalendarClock className="size-3.5" />
              Nothing yet{row.firstDeliveryBy ? ` · first delivery by ${row.firstDeliveryBy}` : ""}
            </span>
          )}
        </span>
      </span>
      <ChevronRight className="size-5 self-center text-muted-foreground" />
    </Button>
  );
}

function Empty({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-card px-6 py-10 text-center">
      <Truck className="size-10 text-muted-foreground/50" />
      <p className="font-semibold">{title}</p>
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
